import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  getShowBillOfMaterialsMgmtRequestQueryKey,
  useShowBillOfMaterialsMgmtRequest,
  useUpdateBillOfMaterialsMgmtRequest,
} from "../../../../../__generated__/orval/endpoints/bill-of-materials-mgmt-requests/bill-of-materials-mgmt-requests";
import { ConfirmationModal } from "../../../../../components/molecules/ConfirmationModal";
import { EditBillOfMaterialsMgmtRequestForm } from "../-internal-components/EditBillOfMaterialsMgmtRequestForm";
import {
  buildUpdateBillOfMaterialsMgmtRequestPayload,
  type EditBillOfMaterialsMgmtRequestFormInput,
  type EditBillOfMaterialsMgmtRequestFormSchema,
  editBillOfMaterialsMgmtRequestFormSchema,
} from "../-schemas/editBillOfMaterialsMgmtRequestSchema";

type EditBillOfMaterialsMgmtRequestFormContainerProps = {
  billOfMaterialsMgmtRequestId: string;
  defaultValues: EditBillOfMaterialsMgmtRequestFormInput;
  id: number;
  title: string;
};

const EditBillOfMaterialsMgmtRequestFormContainer: React.FC<
  EditBillOfMaterialsMgmtRequestFormContainerProps
> = ({ billOfMaterialsMgmtRequestId, defaultValues, id, title }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [formData, setFormData] =
    useState<EditBillOfMaterialsMgmtRequestFormSchema | null>(null);

  const { control, handleSubmit } = useForm({
    defaultValues,
    resolver: zodResolver(editBillOfMaterialsMgmtRequestFormSchema),
  });

  const { mutate, isPending } = useUpdateBillOfMaterialsMgmtRequest({
    mutation: {
      onError: () => {
        enqueueSnackbar(t("mmRequestChild.updateErrorMessage"), {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getShowBillOfMaterialsMgmtRequestQueryKey(id),
        });
        enqueueSnackbar(t("mmRequestChild.updateSuccessMessage"), {
          variant: "success",
        });
        navigate({
          params: { billOfMaterialsMgmtRequestId },
          to: "/vbuyer/bill_of_materials_mgmt_requests/$billOfMaterialsMgmtRequestId",
        });
      },
    },
  });

  const onSubmit: SubmitHandler<EditBillOfMaterialsMgmtRequestFormSchema> =
    useCallback((values) => {
      setFormData(values);
      setOpenConfirmModal(true);
    }, []);

  const handleConfirm = useCallback(() => {
    if (!formData) return;

    setOpenConfirmModal(false);

    const payload = buildUpdateBillOfMaterialsMgmtRequestPayload(formData);

    mutate({
      data: payload,
      id,
    });
  }, [formData, id, mutate]);

  return (
    <PageContainer
      backTo={`/vbuyer/bill_of_materials_mgmt_requests/${billOfMaterialsMgmtRequestId}`}
      showBackButton
      title={title}
    >
      <AppCard>
        <EditBillOfMaterialsMgmtRequestForm
          control={control}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="success"
              loading={isPending}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </EditBillOfMaterialsMgmtRequestForm>
      </AppCard>

      <ConfirmationModal
        handleOk={handleConfirm}
        message={t("mmRequestChild.updateModalMessage")}
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        title={t("mmRequestChild.updateModalTitle")}
      />
    </PageContainer>
  );
};

const EditBillOfMaterialsMgmtRequest: React.FC = () => {
  const { t } = useTranslation();
  const { billOfMaterialsMgmtRequestId } = Route.useParams();
  const id = Number(billOfMaterialsMgmtRequestId);

  const { data, isLoading, isError } = useShowBillOfMaterialsMgmtRequest(id);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.fetchError"), { variant: "error" });
    }
  }, [isError, t]);

  const mmRequestId = data?.mm_request?.id;
  const title = mmRequestId
    ? `${t("common.billOfMaterialsMgmtRequest")} #${mmRequestId}`
    : t("common.billOfMaterialsMgmtRequest");

  if (isLoading || !data?.mm_request) {
    return (
      <PageContainer
        backTo={`/vbuyer/bill_of_materials_mgmt_requests/${billOfMaterialsMgmtRequestId}`}
        showBackButton
        title={title}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  const mmRequest = data.mm_request;

  const defaultValues: EditBillOfMaterialsMgmtRequestFormInput = {
    description: mmRequest.description ?? "",
    previous_ticket: mmRequest.previous_ticket_id
      ? String(mmRequest.previous_ticket_id)
      : "",
    quantity_of_materials: mmRequest.quantity_of_materials ?? "",
    quantity_of_tags:
      mmRequest.quantity_of_tags != null
        ? String(mmRequest.quantity_of_tags)
        : "",
    requester_field: mmRequest.requester_field
      ? { id: mmRequest.requester_field, name: mmRequest.requester_field }
      : null,
    subject: mmRequest.subject ?? "",
  };

  return (
    <EditBillOfMaterialsMgmtRequestFormContainer
      billOfMaterialsMgmtRequestId={billOfMaterialsMgmtRequestId}
      defaultValues={defaultValues}
      id={id}
      title={title}
    />
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/bill_of_materials_mgmt_requests/$billOfMaterialsMgmtRequestId/edit",
)({
  component: EditBillOfMaterialsMgmtRequest,
});
